import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  FileText, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Trash2,
  Save,
  FileUp,
  Edit2,
  Check,
  X
} from 'lucide-react';
import { useExpenses } from '../contexts/ExpenseContext';
import { TravelSubcategory, TRAVEL_SUBCATEGORIES, getLocalDate } from '../types';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore - Vite URL import for worker
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Set up PDF.js worker - use bundled worker via Vite
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface ParsedTravel {
  id: string;
  date: string;
  subcategory: TravelSubcategory;
  description: string;
  amount: number;
  provider: string;
  selected: boolean;
}

export default function ImportTravel() {
  const { addExpense, expenses } = useExpenses();
  const [pdfText, setPdfText] = useState('');
  const [parsedTravel, setParsedTravel] = useState<ParsedTravel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState<{ success: number; errors: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Target tax year for import
  const currentYear = new Date().getFullYear();
  const [targetYear, setTargetYear] = useState<number | 'auto'>('auto');
  const availableYears = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);

  // Update a parsed travel item
  const updateTravel = (id: string, updates: Partial<ParsedTravel>) => {
    setParsedTravel(prev => prev.map(t => 
      t.id === id ? { ...t, ...updates } : t
    ));
  };

  // Check for duplicates
  const isDuplicate = useCallback((date: string, amount: number): boolean => {
    return expenses.some(exp => 
      exp.date === date && 
      exp.category === 'travel' &&
      Math.abs(exp.amount! - amount) < 0.01
    );
  }, [expenses]);

  // Parse Uber receipts
  const parseUber = (section: string): ParsedTravel | null => {
    try {
      if (!section.includes('Uber') && !section.includes('uber.com')) return null;
      console.log('Uber: Found Uber content, parsing...');
      
      const months: Record<string, string> = {
        'January': '01', 'February': '02', 'March': '03', 'April': '04',
        'May': '05', 'June': '06', 'July': '07', 'August': '08',
        'September': '09', 'October': '10', 'November': '11', 'December': '12',
        'Dec': '12', 'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
        'Jun': '06', 'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11'
      };
      
      // Extract date - various formats
      let formattedDate = '';
      // "December 5, 2025" or "Dec 5, 2025"
      const dateMatch1 = section.match(/(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2}),?\s+(\d{4})/i);
      // "12/5/25" or "12/5/25 5:26 PM"
      const dateMatch2 = section.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:\s+\d{1,2}:\d{2})?/);
      
      if (dateMatch1) {
        const monthKey = dateMatch1[1].length <= 3 ? dateMatch1[1] : dateMatch1[1];
        const month = months[monthKey] || months[dateMatch1[1].substring(0, 3)];
        formattedDate = `${dateMatch1[3]}-${month}-${dateMatch1[2].padStart(2, '0')}`;
      } else if (dateMatch2) {
        const year = dateMatch2[3].length === 2 ? '20' + dateMatch2[3] : dateMatch2[3];
        formattedDate = `${year}-${dateMatch2[1].padStart(2, '0')}-${dateMatch2[2].padStart(2, '0')}`;
      }
      console.log('Uber: Date:', formattedDate);
      
      if (!formattedDate) return null;
      
      // Extract total - various formats
      // "Total $36.77" or "$36.77" after Mastercard or payment
      let totalMatch = section.match(/Total\s*\$\s*([\d,]+\.?\d{2})/i);
      if (!totalMatch) {
        // Mastercard/payment format: "Mastercard ••••4683 $36.77"
        totalMatch = section.match(/(?:Mastercard|Visa|card)[^\$]*\$\s*([\d,]+\.\d{2})/i);
      }
      if (!totalMatch) {
        // Look for "$36.77" followed by date pattern (payment line)
        totalMatch = section.match(/\$\s*([\d,]+\.\d{2})\s+\d{1,2}\/\d{1,2}\/\d{2}/);
      }
      if (!totalMatch) {
        // Standalone amount at start: "••••4683 $36.77"
        totalMatch = section.match(/\d{4}\s+\$\s*([\d,]+\.\d{2})/);
      }
      console.log('Uber: Total match:', totalMatch?.[1]);
      
      if (!totalMatch) return null;
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      if (total <= 0) return null;
      
      // Extract driver name - "You rode with OSCAR"
      const driverMatch = section.match(/(?:You rode with|rode with)\s+([A-Z]+)/i);
      const driver = driverMatch ? driverMatch[1] : '';
      console.log('Uber: Driver:', driver);
      
      // Extract trip details - "17.05 miles, 31 minutes"
      const tripMatch = section.match(/([\d.]+)\s*miles?,?\s*(\d+)\s*minutes?/i);
      const miles = tripMatch ? tripMatch[1] : '';
      
      let description = 'Uber';
      if (driver) description += ` - ${driver}`;
      if (miles) description += ` (${miles} mi)`;
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'uber',
        description,
        amount: total,
        provider: 'Uber',
        selected: true
      };
    } catch (e) {
      console.error('Uber parse error:', e);
      return null;
    }
  };

  // Parse Lyft receipts
  const parseLyft = (section: string): ParsedTravel | null => {
    try {
      if (!section.toLowerCase().includes('lyft')) return null;
      
      const monthsMap: Record<string, string> = {
        'january': '01', 'february': '02', 'march': '03', 'april': '04',
        'may': '05', 'june': '06', 'july': '07', 'august': '08',
        'september': '09', 'october': '10', 'november': '11', 'december': '12'
      };
      
      // Extract date - "November 1, 2025" or "ON NOVEMBER 1, 2025"
      let formattedDate = '';
      const dateMatch1 = section.match(/(?:on\s+)?(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2}),?\s+(\d{4})/i);
      const dateMatch2 = section.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      
      if (dateMatch1) {
        const monthKey = dateMatch1[1].toLowerCase();
        formattedDate = `${dateMatch1[3]}-${monthsMap[monthKey]}-${dateMatch1[2].padStart(2, '0')}`;
      } else if (dateMatch2) {
        const year = dateMatch2[3].length === 2 ? '20' + dateMatch2[3] : dateMatch2[3];
        formattedDate = `${year}-${dateMatch2[1].padStart(2, '0')}-${dateMatch2[2].padStart(2, '0')}`;
      }
      
      if (!formattedDate) return null;
      
      // Extract total - "Pay Apple Pay (MasterCard) $24.97" or "$24.97"
      const totalMatch = section.match(/Pay[^$]*\$\s*([\d,]+\.?\d{2})/i) ||
                        section.match(/\$\s*([\d,]+\.?\d{2})/);
      if (!totalMatch) return null;
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      if (total <= 0) return null;
      
      // Extract driver name - "Thanks for riding with Kayne!"
      const driverMatch = section.match(/(?:Thanks for riding with|riding with|ride with)\s+([A-Za-z]+)/i);
      const driver = driverMatch ? driverMatch[1] : '';
      
      // Try to get destination from "YOUR RIDE TO..."
      const destMatch = section.match(/YOUR RIDE TO\s+([^\n]+?)(?:\s+ON\s+)/i);
      const dest = destMatch ? destMatch[1].trim().substring(0, 30) : '';
      
      let description = 'Lyft';
      if (driver) description += ` - ${driver}`;
      if (dest) description += ` to ${dest}`;
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'lyft',
        description,
        amount: total,
        provider: 'Lyft',
        selected: true
      };
    } catch (e) {
      console.error('Lyft parse error:', e);
      return null;
    }
  };

  // Parse car rental receipts (Budget, National, Enterprise, etc.)
  const parseCarRental = (section: string): ParsedTravel | null => {
    try {
      const isBudget = section.includes('Budget') || section.includes('BUDGET') || section.includes('~Budget');
      const isNational = section.includes('National') || section.includes('NATIONAL');
      const isEnterprise = section.includes('Enterprise') || section.includes('ENTERPRISE');
      
      if (!isBudget && !isNational && !isEnterprise) {
        console.log('Car rental: No provider match');
        return null;
      }
      const provider = isBudget ? 'Budget' : isNational ? 'National' : 'Enterprise';
      console.log('Car rental: Provider found -', provider);
      console.log('Car rental: Text length:', section.length);
      console.log('Car rental: Has $ 213:', section.includes('213'));
      
      const months: Record<string, string> = {
        'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06',
        'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12',
        'January': '01', 'February': '02', 'March': '03', 'April': '04',
        'June': '06', 'July': '07', 'August': '08', 'September': '09',
        'October': '10', 'November': '11', 'December': '12',
        'NOV': '11', 'DEC': '12', 'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
        'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08', 'SEP': '09', 'OCT': '10'
      };
      
      // Extract date - various formats
      let formattedDate = '';
      
      // Format: "Thu, Nov 20, 2025" or "Nov 20, 2025"
      const dateMatch1 = section.match(/(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)?,?\s*(\w{3})\s+(\d{1,2}),?\s+(\d{4})/i);
      // Format: "NOV 18,2025@8:06PM" - Budget format with @ symbol
      const dateMatch2 = section.match(/([A-Z]{3})\s*(\d{1,2}),?\s*(\d{4})(?:@|$|\s)/i);
      // Format: "11/17/2025" or "11/17/25"
      const dateMatch3 = section.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      
      const dateMatch = dateMatch1 || dateMatch2 || dateMatch3;
      
      if (dateMatch) {
        if (dateMatch3 && dateMatch === dateMatch3) {
          // MM/DD/YYYY format
          const year = dateMatch[3].length === 2 ? `20${dateMatch[3]}` : dateMatch[3];
          formattedDate = `${year}-${dateMatch[1].padStart(2, '0')}-${dateMatch[2].padStart(2, '0')}`;
        } else {
          // Month name format
          const monthKey = dateMatch[1].substring(0, 3);
          const month = months[monthKey] || months[dateMatch[1].toUpperCase()];
          if (month) {
            formattedDate = `${dateMatch[3]}-${month}-${dateMatch[2].padStart(2, '0')}`;
          }
        }
      }
      
      // National/Enterprise: if no date found yet, try other patterns
      if (!formattedDate && (isNational || isEnterprise)) {
        // Try "October 10, 2025" or "on October 10, 2025" format (full month name)
        const fullMonthMatch = section.match(/(?:on\s+)?(\w+)\s+(\d{1,2}),?\s+(\d{4})/i);
        if (fullMonthMatch) {
          const monthName = fullMonthMatch[1];
          const day = fullMonthMatch[2];
          const year = fullMonthMatch[3];
          const month = months[monthName] || months[monthName.substring(0, 3)];
          if (month) {
            formattedDate = `${year}-${month}-${day.padStart(2, '0')}`;
            console.log('Car rental: Matched full month date:', formattedDate);
          }
        }
        
        // Try MM/DD/YY format anywhere in text
        if (!formattedDate) {
          const fallbackDateMatch = section.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
          if (fallbackDateMatch) {
            const year = fallbackDateMatch[3].length === 2 ? `20${fallbackDateMatch[3]}` : fallbackDateMatch[3];
            formattedDate = `${year}-${fallbackDateMatch[1].padStart(2, '0')}-${fallbackDateMatch[2].padStart(2, '0')}`;
          }
        }
      }
      
      if (!formattedDate) return null;
      
      // Extract total - various formats
      // For Budget: prioritize Net Charges which is the final total
      let totalMatch = null;
      
      // Budget specific: look for "Net Charges:" followed by amount (may be on different lines)
      if (isBudget) {
        // First try direct match: "Net Charges: 75.44" or "Net Charges: USD 75.44"
        totalMatch = section.match(/Net\s*Charges[:\s]*(?:USD)?\s*\$?\s*([\d,]+\.?\d{2})/i);
        
        // Budget linearized format: "Net Charges:\nYour Total Due:\n...\n75.44\n0.00"
        // The pattern is: labels come first, then amounts in same order
        if (!totalMatch) {
          // Look for "75.44" followed by "0.00" pattern (Net Charges then Total Due)
          const amountPairMatch = section.match(/(\d{2,3}\.\d{2})\s+(?:\d+\.\d{2}\s+)?0\.00/);
          if (amountPairMatch && parseFloat(amountPairMatch[1]) > 20) {
            totalMatch = [amountPairMatch[1], amountPairMatch[1]];
            console.log('Budget: Found amount pair pattern:', amountPairMatch[1]);
          }
        }
        
        // Also try: find the largest reasonable amount in the document (likely the total)
        if (!totalMatch) {
          const allAmounts = section.match(/\b(\d{2,3}\.\d{2})\b/g);
          if (allAmounts) {
            let maxAmount = 0;
            for (const amt of allAmounts) {
              const val = parseFloat(amt);
              // Look for amounts between $50 and $500 (typical car rental range)
              if (val > 50 && val < 500 && val > maxAmount) {
                maxAmount = val;
              }
            }
            if (maxAmount > 0) {
              totalMatch = [maxAmount.toFixed(2), maxAmount.toFixed(2)];
              console.log('Budget: Using max amount:', maxAmount);
            }
          }
        }
      }
      
      // National/Enterprise patterns
      if (!totalMatch) {
        console.log('Car rental: Trying National/Enterprise patterns');
        
        // "Total $197.82" - simple Total followed by amount
        totalMatch = section.match(/Total\s*\$\s*([\d,]+\.\d{2})/i);
        if (totalMatch) console.log('Car rental: Matched Total $amount');
        
        if (!totalMatch) {
          totalMatch = section.match(/Total\s*Amount\s*Charged[:\s|]*\$?\s*([\d,]+\.?\d{2})/i);
          if (totalMatch) console.log('Car rental: Matched Total Amount Charged');
        }
        
        if (!totalMatch) {
          // "$ 213.06" standalone on a line
          totalMatch = section.match(/^\s*\$\s*([\d,]+\.\d{2})\s*$/m);
          if (totalMatch) console.log('Car rental: Matched $ amount on line');
        }
        
        if (!totalMatch) {
          // TOTAL (Subject to Audit) pattern - may be on separate lines
          const totalIdx = section.search(/TOTAL\s*\(Subject to Audit\)/i);
          if (totalIdx !== -1) {
            const afterTotal = section.substring(totalIdx, totalIdx + 100);
            const amtMatch = afterTotal.match(/\$\s*([\d,]+\.\d{2})/);
            if (amtMatch) {
              totalMatch = amtMatch;
              console.log('Car rental: Matched TOTAL Subject to Audit');
            }
          }
        }
        
        if (!totalMatch) {
          totalMatch = section.match(/Your\s*Total\s*Charges[:\s|]*\$?\s*([\d,]+\.?\d{2})/i);
        }
      }
      
      
      // Last resort: look for USD followed by amount
      if (!totalMatch) {
        totalMatch = section.match(/USD\s*([\d,]+\.?\d{2})/i);
      }
      
      if (!totalMatch) {
        console.log('Car rental: No total found');
        return null;
      }
      console.log('Car rental: Total match:', totalMatch[1]);
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      if (total === 0) return null;
      
      // Extract locations from various formats
      let pickupLoc = '';
      let returnLoc = '';
      
      // National format: "Airport (CODE)"
      const pickupAirport = section.match(/(?:PICK\s*UP|Pickup)[^(]*\(([A-Z]{3})\)/i);
      const returnAirport = section.match(/RETURN[^(]*\(([A-Z]{3})\)/i);
      
      // Budget format: city names from addresses - handle OCR issues (Ml vs MI)
      // "GRAND RAPIDS,MI,49512" or "GRAND RAPIDS,Ml,49512"
      const budgetPickup = section.match(/Pickup (?:Date\/Time|Location)[:\s]*[^\n]*?([A-Z][A-Z\s]+),\s*([A-Za-z]{2}),?\s*\d{5}/im) ||
                          section.match(/GERALD R\.?FORD|GRAND RAPIDS/i);
      const budgetReturn = section.match(/Return (?:Date\/Time|Location)[:\s]*[^\n]*?([A-Z][A-Z\s]+),\s*([A-Za-z]{2}),?\s*\d{5}/im) ||
                          section.match(/SOUTH BEND/i);
      
      if (pickupAirport) {
        pickupLoc = pickupAirport[1];
      } else if (budgetPickup && budgetPickup[1]) {
        pickupLoc = budgetPickup[1].trim();
      } else if (section.match(/GRAND RAPIDS/i)) {
        pickupLoc = 'Grand Rapids, MI';
      }
      
      if (returnAirport) {
        returnLoc = returnAirport[1];
      } else if (budgetReturn && budgetReturn[1]) {
        returnLoc = budgetReturn[1].trim();
      } else if (section.match(/SOUTH BEND/i) && !section.match(/GRAND RAPIDS/i)) {
        returnLoc = 'South Bend, IN';
      } else if (section.match(/SOUTH BEND/i)) {
        // If both are present, South Bend is likely the return
        returnLoc = 'South Bend, IN';
      }
      
      let locations = '';
      if (pickupLoc && returnLoc && pickupLoc !== returnLoc) {
        locations = ` - ${pickupLoc} to ${returnLoc}`;
      } else if (pickupLoc) {
        locations = ` - ${pickupLoc}`;
      } else if (returnLoc) {
        locations = ` - ${returnLoc}`;
      }
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'rental',
        description: `${provider} Car Rental${locations}`,
        amount: total,
        provider,
        selected: true
      };
    } catch (e) {
      console.error('Car rental parse error:', e);
      return null;
    }
  };

  // Parse flight receipts (Priceline, airlines, etc.)
  const parseFlight = (section: string): ParsedTravel | null => {
    try {
      const isPriceline = section.includes('Priceline') || section.includes('priceline');
      const hasFlightKeywords = section.includes('flight') || section.includes('Flight') || 
                                section.includes('Departure') || section.includes('airline');
      
      if (!isPriceline && !hasFlightKeywords) return null;
      
      // Extract date
      const dateMatch = section.match(/(\w{3})\s+(\d{1,2})[,\s]+(\d{4})/i) ||
                       section.match(/(\w+)\s+(\d{1,2}),\s+(\d{4})/i);
      if (!dateMatch) return null;
      
      const months: Record<string, string> = {
        'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06',
        'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12',
        'January': '01', 'February': '02', 'March': '03', 'April': '04',
        'June': '06', 'July': '07', 'August': '08', 'September': '09',
        'October': '10', 'November': '11', 'December': '12'
      };
      
      const monthKey = dateMatch[1].substring(0, 3);
      const month = months[monthKey] || months[dateMatch[1]];
      const formattedDate = `${dateMatch[3]}-${month}-${dateMatch[2].padStart(2, '0')}`;
      
      // Extract total
      const totalMatch = section.match(/(?:Total Charged|Total|Flight subtotal)[:\s]*\$?\s*([\d,]+\.?\d{2})/i);
      if (!totalMatch) return null;
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      
      // Extract route
      const routeMatch = section.match(/([A-Z]{3})\s*[-–]\s*([A-Z]{3})/);
      const route = routeMatch ? `${routeMatch[1]} to ${routeMatch[2]}` : '';
      
      // Extract airline
      const airlineMatch = section.match(/(American Airlines|United|Delta|Southwest|Spirit|Frontier|JetBlue)/i);
      const airline = airlineMatch ? airlineMatch[1] : 'Flight';
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'airfare',
        description: `${airline}${route ? ' - ' + route : ''}`,
        amount: total,
        provider: airline,
        selected: true
      };
    } catch (e) {
      console.error('Flight parse error:', e);
      return null;
    }
  };

  // Parse payment app transactions (Venmo, Zelle, etc.) for shuttle tips
  const parsePaymentApp = (section: string): ParsedTravel | null => {
    try {
      const hasPaymentKeywords = section.includes('Transaction details') || 
                                 section.includes('Payment sent') ||
                                 section.includes('CAPITAL ONE');
      
      if (!hasPaymentKeywords) return null;
      
      // Look for shuttle/tip keywords
      const isShuttle = section.match(/shuttle|tip|transport/i);
      if (!isShuttle) return null;
      
      // Extract amount (usually prominently displayed)
      const amountMatch = section.match(/\$\s*([\d,]+\.?\d{2})/);
      if (!amountMatch) return null;
      const amount = parseFloat(amountMatch[1].replace(/,/g, ''));
      
      // Extract date (various formats)
      const dateMatch = section.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/) ||
                       section.match(/(Yesterday|Today)/i);
      
      let formattedDate = '';
      if (dateMatch) {
        if (dateMatch[1] === 'Yesterday') {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          formattedDate = yesterday.toISOString().split('T')[0];
        } else if (dateMatch[1] === 'Today') {
          formattedDate = getLocalDate();
        } else {
          const parts = dateMatch[1].split('/');
          const year = parts[2].length === 2 ? '20' + parts[2] : parts[2];
          formattedDate = `${year}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        }
      }
      
      // Extract recipient
      const recipientMatch = section.match(/(?:To:|For)\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
      const recipient = recipientMatch ? recipientMatch[1] : '';
      
      // Extract description/note
      const noteMatch = section.match(/For\s+([^\n$]+)/i);
      const note = noteMatch ? noteMatch[1].trim() : 'Shuttle/Transport';
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'other',
        description: `${note}${recipient ? ' - ' + recipient : ''}`,
        amount,
        provider: 'Payment',
        selected: true
      };
    } catch (e) {
      console.error('Payment app parse error:', e);
      return null;
    }
  };

  // Parse hotel/lodging receipts
  const parseHotel = (section: string): ParsedTravel | null => {
    try {
      // Skip if this is a payment app transaction (PayPal, Venmo, Zelle, Cash App)
      if (section.includes('PayPal') || section.includes('Venmo') || 
          section.includes('Zelle') || section.includes('Cash App') ||
          section.includes('Transaction ID') || section.includes('Money Sent')) {
        return null;
      }
      
      // Check for hotel keywords - must be hotel-specific, not just address matches
      const hotelKeywords = ['CountrySide Inn', 'Hampton Inn', 'Holiday Inn', 'Comfort Inn', 
                            'Comfort Suites', 'Best Western', 'Days Inn', 'Super 8', 
                            'La Quinta', 'Quality Inn', 'Sleep Inn', 'Motel', 'Lodge', 'Resort',
                            'Folio No', 'Room', 'Date In', 'Date Out', 'Check-In', 'Check-Out',
                            'Rental Charge', 'Room Rate'];
      const hasHotelKeyword = hotelKeywords.some(kw => section.toLowerCase().includes(kw.toLowerCase()));
      if (!hasHotelKeyword) return null;
      
      console.log('Hotel: Found hotel content, parsing...');
      
      const months: Record<string, string> = {
        'January': '01', 'February': '02', 'March': '03', 'April': '04',
        'May': '05', 'June': '06', 'July': '07', 'August': '08',
        'September': '09', 'October': '10', 'November': '11', 'December': '12',
        'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'Jun': '06',
        'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
      };
      
      // Extract hotel name - usually at the top
      let hotelName = '';
      const hotelMatch = section.match(/(CountrySide\s*Inn|Hampton\s*Inn|Holiday\s*Inn|Comfort\s*(?:Inn|Suites)|Best\s*Western|Days\s*Inn|Super\s*8|La\s*Quinta|Quality\s*Inn|Sleep\s*Inn|Marriott|Hilton|Hyatt|Radisson|Wyndham|Choice\s*Hotels)/i) ||
                        section.match(/^([A-Z][A-Za-z\s]+(?:Inn|Hotel|Motel|Suites|Lodge|Resort))/m);
      if (hotelMatch) {
        hotelName = hotelMatch[1].trim();
      }
      console.log('Hotel: Name:', hotelName);
      
      // Extract date - various formats
      let formattedDate = '';
      // "Jul/16/2025" or "Date In Jul/16/2025"
      const dateMatch1 = section.match(/(?:Date\s*(?:In|Out)|Check[- ]?(?:In|Out))[:\s]*([A-Za-z]+)\/(\d{1,2})\/(\d{4})/i);
      // "Jul/16/2025" standalone
      const dateMatch2 = section.match(/([A-Za-z]{3})\/(\d{1,2})\/(\d{4})/);
      // "07/16/2025"
      const dateMatch3 = section.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      // "July 16, 2025"
      const dateMatch4 = section.match(/(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2}),?\s+(\d{4})/i);
      
      if (dateMatch1) {
        const month = months[dateMatch1[1]] || dateMatch1[1];
        formattedDate = `${dateMatch1[3]}-${month}-${dateMatch1[2].padStart(2, '0')}`;
      } else if (dateMatch4) {
        const month = months[dateMatch4[1]] || months[dateMatch4[1].substring(0, 3)];
        formattedDate = `${dateMatch4[3]}-${month}-${dateMatch4[2].padStart(2, '0')}`;
      } else if (dateMatch2) {
        const month = months[dateMatch2[1]];
        if (month) {
          formattedDate = `${dateMatch2[3]}-${month}-${dateMatch2[2].padStart(2, '0')}`;
        }
      } else if (dateMatch3) {
        formattedDate = `${dateMatch3[3]}-${dateMatch3[1].padStart(2, '0')}-${dateMatch3[2].padStart(2, '0')}`;
      }
      console.log('Hotel: Date:', formattedDate);
      
      if (!formattedDate) return null;
      
      // Extract total amount
      let totalMatch = section.match(/(?:Total|Balance|Amount\s*Due)[:\s]*\$?\s*([\d,]+\.?\d{2})/i);
      if (!totalMatch) {
        // Look for payment amount: "Master Card ... | 65.00"
        totalMatch = section.match(/(?:Master\s*Card|Visa|Amex|Discover)[^\d]*([\d,]+\.\d{2})/i);
      }
      if (!totalMatch) {
        // Look for any significant amount
        const amounts = section.match(/\d+\.\d{2}/g);
        if (amounts) {
          // Find the largest amount (usually the total)
          const largestAmount = amounts.map(a => parseFloat(a)).sort((a, b) => b - a)[0];
          if (largestAmount > 20) {
            totalMatch = ['', largestAmount.toString()];
          }
        }
      }
      console.log('Hotel: Total match:', totalMatch?.[1]);
      
      if (!totalMatch) return null;
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      if (total <= 0) return null;
      
      // Extract location from address
      let location = '';
      const locationMatch = section.match(/(\w+),\s*(\w+),\s*(\d{5})/); // City, State, ZIP
      if (locationMatch) {
        location = `${locationMatch[1]}, ${locationMatch[2]}`;
      }
      
      // Extract folio/confirmation number
      const folioMatch = section.match(/(?:Folio|Confirmation|Booking)[#:\s]*([A-Z0-9]+)/i);
      const folio = folioMatch ? folioMatch[1] : '';
      
      const description = hotelName 
        ? `${hotelName}${location ? ' - ' + location : ''}${folio ? ' #' + folio : ''}`
        : `Hotel${location ? ' - ' + location : ''}${folio ? ' #' + folio : ''}`;
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'hotel',
        description,
        amount: total,
        provider: hotelName || 'Hotel',
        selected: true
      };
    } catch (e) {
      console.error('Hotel parse error:', e);
      return null;
    }
  };

  // Parse shuttle/transportation service invoices
  const parseShuttle = (section: string): ParsedTravel | null => {
    try {
      // Check for shuttle/transport keywords
      const shuttleKeywords = ['TRANSPORTATION', 'TRANSPORT', 'SHUTTLE', 'YNOTME', 
                               'Houston, TX to', 'to Elmendorf', 'BILL TO'];
      const hasShuttleKeyword = shuttleKeywords.some(kw => section.toUpperCase().includes(kw.toUpperCase()));
      
      // Also check for invoice with route pattern (City, ST to City, ST)
      const hasRoute = /[A-Z][a-z]+,?\s*[A-Z]{2}\s+to\s+[A-Z][a-z]+,?\s*[A-Z]{2}/i.test(section);
      
      // Check for PayPal/Venmo/Zelle payments with shuttle in note
      const isPaymentWithShuttle = (section.includes('PayPal') || section.includes('Venmo') || 
                                    section.includes('Zelle') || section.includes('Cash App') ||
                                    section.includes('Money Sent')) && 
                                   section.toLowerCase().includes('shuttle');
      
      // Check for route patterns like "Fort Wayne to Bristol"
      const hasSimpleRoute = /(?:from\s+)?([A-Z][a-z\s]+)\s+to\s+([A-Z][a-z\s]+)/i.test(section);
      
      if (!hasShuttleKeyword && !hasRoute && !isPaymentWithShuttle && !hasSimpleRoute) return null;
      
      console.log('Shuttle: Found shuttle content, parsing...');
      
      // Extract date - various formats
      let formattedDate = '';
      // "07/14/2025" or "DATE 07/14/2025"
      const dateMatch1 = section.match(/(?:DATE|DUE DATE)?[:\s]*(\d{1,2})\/(\d{1,2})\/(\d{4})/i);
      // "July 14, 2025"
      const dateMatch2 = section.match(/(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2}),?\s+(\d{4})/i);
      
      const months: Record<string, string> = {
        'January': '01', 'February': '02', 'March': '03', 'April': '04',
        'May': '05', 'June': '06', 'July': '07', 'August': '08',
        'September': '09', 'October': '10', 'November': '11', 'December': '12',
        'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'Jun': '06',
        'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
      };
      
      if (dateMatch1) {
        formattedDate = `${dateMatch1[3]}-${dateMatch1[1].padStart(2, '0')}-${dateMatch1[2].padStart(2, '0')}`;
      } else if (dateMatch2) {
        const month = months[dateMatch2[1]] || months[dateMatch2[1].substring(0, 3)];
        formattedDate = `${dateMatch2[3]}-${month}-${dateMatch2[2].padStart(2, '0')}`;
      }
      console.log('Shuttle: Date:', formattedDate);
      
      if (!formattedDate) return null;
      
      // Extract total amount - look for TOTAL line
      let totalMatch = section.match(/TOTAL[:\s]*\$?\s*([\d,]+\.?\d{2})/i);
      if (!totalMatch) {
        // Look for amount after route description
        totalMatch = section.match(/\$\s*([\d,]+\.\d{2})/);
      }
      console.log('Shuttle: Total match:', totalMatch?.[1]);
      
      if (!totalMatch) return null;
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      if (total <= 0) return null;
      
      // Extract route (various formats)
      let route = '';
      // "Houston, TX to Elmendorf, TX" or "Fort Wayne to Bristol"
      const routeMatch = section.match(/(?:shuttle\s+(?:from\s+)?)?([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?(?:,?\s*[A-Z]{2})?)\s+to\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?(?:,?\s*[A-Z]{2})?)/i);
      if (routeMatch) {
        route = `${routeMatch[1].trim()} to ${routeMatch[2].trim()}`;
      }
      console.log('Shuttle: Route:', route);
      
      // Extract company name
      let company = '';
      const companyMatch = section.match(/^([A-Z][A-Z\s]+(?:LLC|INC|CORP|SERVICES|TRANSPORTATION))/im) ||
                          section.match(/(YNOTME[^\n]*)/i);
      if (companyMatch) {
        company = companyMatch[1].trim();
      }
      
      // Extract invoice number
      const invoiceMatch = section.match(/(?:INVOICE|INV)[#:\s]*([A-Z0-9]+)/i);
      const invoice = invoiceMatch ? invoiceMatch[1] : '';
      
      const description = route 
        ? `Shuttle: ${route}${company ? ' - ' + company : ''}${invoice ? ' #' + invoice : ''}`
        : `Shuttle${company ? ': ' + company : ''}${invoice ? ' #' + invoice : ''}`;
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'shuttle',
        description,
        amount: total,
        provider: company || 'Shuttle',
        selected: true
      };
    } catch (e) {
      console.error('Shuttle parse error:', e);
      return null;
    }
  };

  // Parse Greyhound bus receipts
  const parseGreyhound = (section: string): ParsedTravel | null => {
    try {
      if (!section.toLowerCase().includes('greyhound') && !section.includes('GREYHOUND')) return null;
      console.log('Greyhound: Found Greyhound content, parsing...');
      
      const months: Record<string, string> = {
        'January': '01', 'February': '02', 'March': '03', 'April': '04',
        'May': '05', 'June': '06', 'July': '07', 'August': '08',
        'September': '09', 'October': '10', 'November': '11', 'December': '12',
        'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'Jun': '06',
        'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
      };
      
      // Extract date - various formats
      let formattedDate = '';
      // "December 5, 2025" or "Dec 5, 2025"
      const dateMatch1 = section.match(/(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2}),?\s+(\d{4})/i);
      // "12/5/25" or "12/05/2025"
      const dateMatch2 = section.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      // "2025-12-05"
      const dateMatch3 = section.match(/(\d{4})-(\d{2})-(\d{2})/);
      
      if (dateMatch1) {
        const monthKey = dateMatch1[1];
        const month = months[monthKey] || months[monthKey.substring(0, 3)];
        formattedDate = `${dateMatch1[3]}-${month}-${dateMatch1[2].padStart(2, '0')}`;
      } else if (dateMatch3) {
        formattedDate = `${dateMatch3[1]}-${dateMatch3[2]}-${dateMatch3[3]}`;
      } else if (dateMatch2) {
        const year = dateMatch2[3].length === 2 ? '20' + dateMatch2[3] : dateMatch2[3];
        formattedDate = `${year}-${dateMatch2[1].padStart(2, '0')}-${dateMatch2[2].padStart(2, '0')}`;
      }
      console.log('Greyhound: Date:', formattedDate);
      
      if (!formattedDate) {
        // Use today if no date found
        formattedDate = getLocalDate();
      }
      
      // Extract total amount - various formats
      let totalMatch = section.match(/(?:Total|Amount|Paid|Charge)[:\s]*\$\s*([\d,]+\.?\d{2})/i);
      if (!totalMatch) {
        // Look for standalone amounts
        totalMatch = section.match(/\$\s*([\d,]+\.\d{2})/);
      }
      console.log('Greyhound: Total match:', totalMatch?.[1]);
      
      if (!totalMatch) return null;
      const total = parseFloat(totalMatch[1].replace(/,/g, ''));
      if (total <= 0) return null;
      
      // Extract route/destination
      let route = '';
      // Common patterns: "Dallas to Houston" or "From: Dallas To: Houston"
      const routeMatch1 = section.match(/(?:from|depart(?:ing)?)[:\s]*([A-Za-z\s,]+?)(?:\s+to|\s+→|\s+-)\s*([A-Za-z\s,]+)/i);
      const routeMatch2 = section.match(/([A-Z][a-z]+(?:,?\s*[A-Z]{2})?)\s+(?:to|→|-)\s+([A-Z][a-z]+(?:,?\s*[A-Z]{2})?)/);
      
      if (routeMatch1) {
        route = `${routeMatch1[1].trim()} to ${routeMatch1[2].trim()}`;
      } else if (routeMatch2) {
        route = `${routeMatch2[1].trim()} to ${routeMatch2[2].trim()}`;
      }
      
      // Extract confirmation number if present
      const confirmMatch = section.match(/(?:Confirmation|Booking|Order)[#:\s]*([A-Z0-9]+)/i);
      const confirm = confirmMatch ? confirmMatch[1] : '';
      
      const description = route 
        ? `Greyhound: ${route}${confirm ? ' #' + confirm : ''}`
        : `Greyhound Bus${confirm ? ' #' + confirm : ''}`;
      
      return {
        id: crypto.randomUUID(),
        date: formattedDate,
        subcategory: 'other',  // Bus travel
        description,
        amount: total,
        provider: 'Greyhound',
        selected: true
      };
    } catch (e) {
      console.error('Greyhound parse error:', e);
      return null;
    }
  };

  // Master parse function
  const parseTravelText = (text: string) => {
    const travelExpenses: ParsedTravel[] = [];
    
    // First, try parsing the entire text as one receipt
    // This handles cases where provider name appears at the end (like "National Car Rental" in footer)
    const fullTextSections = [text];
    
    // Also split by receipt boundaries for multiple receipts
    const splitSections = text.split(/(?=Your ride with|Transaction details|RENTAL AGREEMENT)/i);
    
    // Combine: try full text first, then split sections
    const sections = [...fullTextSections, ...splitSections.filter(s => s !== text)];
    
    for (const section of sections) {
      if (!section.trim() || section.length < 50) continue;
      
      // Try each parser
      let parsed: ParsedTravel | null = null;
      
      parsed = parseUber(section);
      if (!parsed) parsed = parseLyft(section);
      if (!parsed) parsed = parseCarRental(section);
      if (!parsed) parsed = parseFlight(section);
      if (!parsed) parsed = parseShuttle(section);  // Shuttle before hotel to catch payment app shuttles
      if (!parsed) parsed = parseHotel(section);
      if (!parsed) parsed = parseGreyhound(section);
      if (!parsed) parsed = parsePaymentApp(section);
      
      if (parsed && parsed.amount > 0) {
        // Check for duplicates
        const exists = travelExpenses.some(t => 
          t.date === parsed!.date && Math.abs(t.amount - parsed!.amount) < 0.01
        );
        if (!exists) {
          travelExpenses.push(parsed);
        }
      }
    }
    
    return travelExpenses;
  };

  // Extract text from PDF
  const extractTextFromPDF = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      fullText += pageText + '\n\n';
    }
    
    return fullText;
  };

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    setIsLoading(true);
    setError(null);
    setImportStatus(null);
    
    try {
      const text = await extractTextFromPDF(file);
      setPdfText(text);
      
      const travel = parseTravelText(text);
      if (travel.length === 0) {
        setError('No travel expenses found in PDF. Try pasting the text manually below.');
      } else {
        setParsedTravel(travel);
      }
    } catch (e: any) {
      console.error('PDF extraction error:', e);
      setError(`Failed to read PDF: ${e.message}. Try pasting the text manually.`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1
  });

  // Parse from pasted text
  const handleParseText = () => {
    if (!pdfText.trim()) return;
    
    setError(null);
    const travel = parseTravelText(pdfText);
    if (travel.length === 0) {
      setError('No travel expenses found in the text. Make sure you copied the full receipt content.');
    } else {
      setParsedTravel(travel);
    }
  };

  // Toggle selection
  const toggleSelection = (id: string) => {
    setParsedTravel(prev => 
      prev.map(t => t.id === id ? { ...t, selected: !t.selected } : t)
    );
  };

  // Remove travel expense
  const removeTravel = (id: string) => {
    setParsedTravel(prev => prev.filter(t => t.id !== id));
  };

  // Get provider badge color
  const getProviderColor = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'uber': return 'bg-black/60 text-white border-white/30';
      case 'lyft': return 'bg-pink-500/20 text-pink-400 border-pink-500/50';
      case 'budget': return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 'national': return 'bg-green-500/20 text-green-400 border-green-500/50';
      default: return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
    }
  };

  // Import selected travel expenses
  const handleImport = async () => {
    const selected = parsedTravel.filter(t => t.selected);
    if (selected.length === 0) return;
    
    setIsLoading(true);
    let success = 0;
    let errors = 0;
    let skipped = 0;
    
    for (const travel of selected) {
      // Check for duplicates
      if (isDuplicate(travel.date, travel.amount)) {
        skipped++;
        continue;
      }
      
      try {
        // Determine the date to use - override year if targetYear is set
        let dateToUse = travel.date;
        if (targetYear !== 'auto' && travel.date) {
          const dateParts = travel.date.split('-');
          if (dateParts.length === 3) {
            dateToUse = `${targetYear}-${dateParts[1]}-${dateParts[2]}`;
          }
        }
        
        await addExpense({
          category: 'travel',
          subcategory: travel.subcategory,
          description: travel.description,
          amount: travel.amount,
          date: dateToUse,
          file: new Blob([`${travel.provider} - ${travel.description}`], { type: 'text/plain' }),
          filename: `travel-${travel.provider.toLowerCase()}-${dateToUse}.txt`
        });
        success++;
      } catch (e) {
        console.error('Failed to import travel:', e);
        errors++;
      }
    }
    
    setImportStatus({ success, errors, skipped });
    if (success > 0 || skipped > 0) {
      const importedOrSkipped = selected.map(t => t.id);
      setParsedTravel(prev => prev.filter(t => !importedOrSkipped.includes(t.id)));
    }
    setIsLoading(false);
  };

  const selectedCount = parsedTravel.filter(t => t.selected && !isDuplicate(t.date, t.amount)).length;
  const totalAmount = parsedTravel
    .filter(t => t.selected && !isDuplicate(t.date, t.amount))
    .reduce((sum, t) => sum + t.amount, 0);
  const duplicateCount = parsedTravel.filter(t => isDuplicate(t.date, t.amount)).length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Import Travel Expenses</h1>
        <p className="text-shuttle-sky mt-1">
          Upload receipts from Uber, Lyft, car rentals, flights, and more
        </p>
      </div>

      {/* Target Tax Year Selector */}
      <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-md rounded-2xl p-4 border border-amber-500/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-white flex items-center gap-2">
              📅 Target Tax Year
            </h3>
            <p className="text-xs text-white/60">
              {targetYear === 'auto' 
                ? 'Year will be detected from receipt dates' 
                : `All imports will be saved to ${targetYear}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={targetYear}
              onChange={(e) => setTargetYear(e.target.value === 'auto' ? 'auto' : parseInt(e.target.value))}
              className="px-4 py-2 bg-shuttle-dark border-2 border-amber-500/50 rounded-xl text-white font-medium focus:ring-2 focus:ring-amber-500 transition-all"
            >
              <option value="auto">🔄 Auto-detect from date</option>
              {availableYears.map(year => (
                <option key={year} value={year}>
                  📋 {year} {year < currentYear ? '(Back taxes)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        {targetYear !== 'auto' && targetYear < currentYear && (
          <div className="mt-3 p-2 bg-orange-500/20 rounded-lg border border-orange-500/30">
            <p className="text-sm text-orange-200">
              ⚠️ Importing to <strong>{targetYear}</strong> - make sure these receipts are from that year!
            </p>
          </div>
        )}
      </div>

      {/* Supported Providers */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-black/60 text-white border border-white/30">
          ✓ Uber
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-pink-500/20 text-pink-400 border border-pink-500/50">
          ✓ Lyft
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/50">
          ✓ Budget
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/50">
          ✓ National
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/50">
          ✓ Priceline/Airlines
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/50">
          ✓ Payment Apps
        </span>
      </div>

      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
          isDragActive 
            ? 'border-shuttle-sand bg-shuttle-sand/10' 
            : 'border-white/30 hover:border-white/50 bg-white/5'
        }`}
      >
        <input {...getInputProps()} />
        <FileUp className="w-12 h-12 mx-auto mb-4 text-shuttle-sky" />
        {isDragActive ? (
          <p className="text-white text-lg">Drop the PDF here...</p>
        ) : (
          <>
            <p className="text-white text-lg mb-2">
              Drag & drop your travel receipts PDF here
            </p>
            <p className="text-white/60 text-sm">or click to browse</p>
          </>
        )}
      </div>

      {/* Manual Text Input */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Or Paste Receipt Text
        </h3>
        <textarea
          value={pdfText}
          onChange={(e) => setPdfText(e.target.value)}
          placeholder="Paste your travel receipts from Uber, Lyft, rental car companies, or airlines..."
          className="w-full h-32 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-shuttle-sand resize-none font-mono text-sm"
        />
        <div className="flex gap-3 mt-3">
          <button
            onClick={handleParseText}
            disabled={!pdfText.trim()}
            className="px-6 py-2 bg-shuttle-sky/20 text-shuttle-sky rounded-lg hover:bg-shuttle-sky/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Parse Text
          </button>
          <button
            onClick={() => { setPdfText(''); setParsedTravel([]); setError(null); setImportStatus(null); }}
            disabled={!pdfText.trim() && parsedTravel.length === 0}
            className="px-6 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-shuttle-sand" />
          <span className="ml-3 text-white">Processing...</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {/* Import Status */}
      {importStatus && (
        <div className={`${importStatus.success > 0 ? 'bg-green-500/20 border-green-500/50' : 'bg-yellow-500/20 border-yellow-500/50'} border rounded-xl p-4 flex items-start gap-3`}>
          <CheckCircle className={`w-5 h-5 ${importStatus.success > 0 ? 'text-green-400' : 'text-yellow-400'} flex-shrink-0 mt-0.5`} />
          <div>
            <p className={importStatus.success > 0 ? 'text-green-200' : 'text-yellow-200'}>
              {importStatus.success > 0 && `Successfully imported ${importStatus.success} travel expense(s)!`}
              {importStatus.errors > 0 && ` ${importStatus.errors} failed.`}
            </p>
            {importStatus.skipped > 0 && (
              <p className="text-yellow-300 text-sm mt-1">
                ⚠️ {importStatus.skipped} duplicate(s) skipped - already in database!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Parsed Travel Expenses */}
      {parsedTravel.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-medium text-white">
                Found {parsedTravel.length} Travel Expense(s)
              </h3>
              {duplicateCount > 0 && (
                <p className="text-yellow-400 text-sm">
                  ⚠️ {duplicateCount} already in database
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span className="text-shuttle-sky">
                {selectedCount} new • ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
              <button
                onClick={handleImport}
                disabled={selectedCount === 0 || isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-lg font-medium hover:from-amber-400 hover:to-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Save className="w-4 h-4" />
                Import Selected
              </button>
            </div>
          </div>

          <div className="divide-y divide-white/10">
            {parsedTravel.map((travel) => {
              const isExisting = isDuplicate(travel.date, travel.amount);
              return (
              <div
                key={travel.id}
                className={`p-4 transition-colors ${
                  isExisting ? 'bg-yellow-500/10 opacity-60' : travel.selected ? 'bg-white/5' : 'bg-transparent opacity-50'
                }`}
              >
                {editingId === travel.id ? (
                  /* Edit Mode */
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <select
                        value={travel.subcategory}
                        onChange={(e) => updateTravel(travel.id, { subcategory: e.target.value as TravelSubcategory })}
                        className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      >
                        {TRAVEL_SUBCATEGORIES.map(cat => (
                          <option key={cat.value} value={cat.value} className="bg-slate-800">
                            {cat.icon} {cat.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="date"
                        value={travel.date}
                        onChange={(e) => updateTravel(travel.id, { date: e.target.value })}
                        className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                      <div className="flex items-center gap-1">
                        <span className="text-white">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={travel.amount}
                          onChange={(e) => updateTravel(travel.id, { amount: parseFloat(e.target.value) || 0 })}
                          className="w-24 px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                      </div>
                    </div>
                    <input
                      type="text"
                      value={travel.description}
                      onChange={(e) => updateTravel(travel.id, { description: e.target.value })}
                      className="w-full px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder="Description"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingId(null)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-sm"
                      >
                        <Check className="w-4 h-4" /> Done
                      </button>
                      <button
                        onClick={() => removeTravel(travel.id)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
                      >
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={travel.selected && !isExisting}
                      onChange={() => !isExisting && toggleSelection(travel.id)}
                      disabled={isExisting}
                      className={`w-5 h-5 rounded border-white/30 bg-white/10 text-shuttle-sand focus:ring-shuttle-sand ${isExisting ? 'cursor-not-allowed opacity-50' : ''}`}
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-2xl">
                          {isExisting ? '⚠️' : TRAVEL_SUBCATEGORIES.find(c => c.value === travel.subcategory)?.icon || '💰'}
                        </span>
                        {isExisting && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium border bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                            DUPLICATE
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getProviderColor(travel.provider)}`}>
                          {travel.provider}
                        </span>
                        <span className="text-white/50">•</span>
                        <span className="text-white/70 capitalize text-sm">
                          {travel.subcategory}
                        </span>
                        <span className="text-white/50">•</span>
                        <span className="text-white/70 text-sm">
                          {travel.date}
                        </span>
                      </div>
                      <p className="text-white/80 text-sm truncate">
                        {travel.description}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-xl font-bold text-red-400">
                        -${travel.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                    </div>

                    <button
                      onClick={() => setEditingId(travel.id)}
                      className="p-2 text-white/40 hover:text-orange-400 transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>

                    <button
                      onClick={() => removeTravel(travel.id)}
                      className="p-2 text-white/40 hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            );
            })}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h3 className="text-lg font-medium text-white mb-3">🚗 Supported Travel Formats</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-black/30 rounded-lg p-4 border border-white/20">
            <h4 className="font-medium text-white mb-2">🚗 Ride Shares</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Uber receipts (email)</li>
              <li>• Lyft receipts (email)</li>
              <li>• Driver, route, & totals</li>
            </ul>
          </div>
          <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-500/30">
            <h4 className="font-medium text-orange-400 mb-2">🚙 Car Rentals</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Budget rental agreements</li>
              <li>• National rental agreements</li>
              <li>• Pickup/return locations</li>
            </ul>
          </div>
          <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
            <h4 className="font-medium text-blue-400 mb-2">✈️ Flights & Other</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Priceline bookings</li>
              <li>• Airline receipts</li>
              <li>• Shuttle payments (Venmo/Zelle)</li>
            </ul>
          </div>
        </div>
        <p className="mt-4 text-white/50 text-xs">
          💡 Paste or upload your receipts - the parser auto-detects the format!
        </p>
      </div>
    </div>
  );
}

